// Cellery Soapbox — edit-mode placeholders for script-generated containers.
//
// The edit frame runs script-free (CONTRACTS §6c), so what an editor sees IS the
// authored markup. For a script-driven deck that means the containers its script
// fills at read time render as empty space, with nothing to say why. This marks
// those containers so edit mode shows where generated content will appear.
//
// Two deliberate constraints, both about never polluting the saved bytes:
//   1. Marking is an ATTRIBUTE (data-soapbox-empty), never an inserted node, so
//      no placeholder text can end up inside authored elements.
//   2. The label is drawn with a ::after rule from ONE injected stylesheet tagged
//      data-soapbox, which serializeCleanHtml strips wholesale.

const STYLE_ID = "soapbox-edit-placeholders";
const MARK_ATTR = "data-soapbox-empty";

/** An element the author left empty for its script to fill. */
function isEmptyContainer(el: Element): boolean {
  if (!el.id) return false;
  if (el.children.length > 0) return false;
  return (el.textContent ?? "").trim().length === 0;
}

/**
 * Mark every empty, id-bearing element whose id is referenced from one of the
 * document's own scripts. Referenced-by-id is the signal that the emptiness is
 * intentional (a fill target) rather than incidental.
 *
 * Returns the number of containers marked.
 */
export function markGeneratedContainers(doc: Document): number {
  const scriptText = Array.from(doc.querySelectorAll("script"))
    .map((s) => s.textContent ?? "")
    .join("\n");
  if (!scriptText.trim()) return 0;

  let marked = 0;
  for (const el of Array.from(doc.querySelectorAll("[id]"))) {
    if (!isEmptyContainer(el)) continue;
    if (!scriptText.includes(el.id)) continue;
    el.setAttribute(MARK_ATTR, `#${el.id}`);
    marked += 1;
  }
  if (marked > 0) ensurePlaceholderStyles(doc);
  return marked;
}

/** Remove all placeholder marking and the injected stylesheet. */
export function clearGeneratedContainers(doc: Document): void {
  for (const el of Array.from(doc.querySelectorAll(`[${MARK_ATTR}]`))) {
    el.removeAttribute(MARK_ATTR);
  }
  doc.getElementById(STYLE_ID)?.remove();
}

function ensurePlaceholderStyles(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.setAttribute("data-soapbox", "edit-placeholders");
  // Dashed outline + a ::after label. min-height so a zero-height container is
  // still visible; the box is inert because its content is generated elsewhere.
  style.textContent = `
    [${MARK_ATTR}] {
      display: block;
      min-height: 72px;
      border: 1px dashed rgba(0, 104, 98, 0.5);
      border-radius: 8px;
      background: repeating-linear-gradient(
        45deg,
        rgba(0, 104, 98, 0.04),
        rgba(0, 104, 98, 0.04) 8px,
        transparent 8px,
        transparent 16px
      );
    }
    [${MARK_ATTR}]::after {
      content: "generated when the doc is read: " attr(${MARK_ATTR});
      display: block;
      padding: 10px 12px;
      font: 500 12px/1.4 ui-sans-serif, system-ui, sans-serif;
      letter-spacing: 0.02em;
      color: rgba(0, 79, 74, 0.75);
    }
  `;
  (doc.head ?? doc.documentElement).appendChild(style);
}
